import { callAccedoAPI } from "../../../axios/accedoApi";
import { callAPI } from "../../../axios/index";
import {
  LIT_CONTROL_HISTORY_REQUEST, LIT_CONTROL_HISTORY_SUCCESS, LIT_CONTROL_HISTORY_FAILURE,
  LIT_APP_LOGOUT_REQUEST, LIT_APP_LOGOUT_SUCCESS, LIT_APP_LOGOUT_FAILURE, LIT_APP_LOGOUT_STATUS, LIT_APP_LOGOUT_STATUS_FAILURE,
  LIT_CLEAR_CACHE_REQUEST, LIT_CLEAR_CACHE_SUCCESS, LIT_CLEAR_CACHE_FAILURE, LIT_CLEAR_CACHE_STATUS, LIT_CLEAR_CACHE_STATUS_FAILURE,
  LIT_ACCOUNT_REFRESH_REQUEST, LIT_ACCOUNT_REFRESH_SUCCESS, LIT_ACCOUNT_REFRESH_FAILURE, LIT_ACCOUNT_REFRESH_STATUS,
} from "../reducers/litControlReducer";

const accedoBase = () => localStorage.getItem("accedo_url") || "";

const currentUserId = () => JSON.parse(localStorage.getItem("user_id"))?.userName;

/**
 * 1.7 POST /lit/control — unified control endpoint.
 * Dashboard API auth. Only the documented fields are sent:
 * { SID, deviceId, userId, ticketId? (optional), action } where action is the
 * enum "clearCache" | "logout". Response: { code, body: { controlRequestID, action }, message }.
 */
const sendLitControl = ({ accountId, deviceId, ticketId, action }) => {
  const body = {
    SID: accountId,
    deviceId,
    userId: currentUserId(),
    action,
  };
  if (ticketId) body.ticketId = ticketId;
  return callAccedoAPI(`${accedoBase()}/lit/control`, "POST", body);
};

/**
 * 1.11 GET /lit/control/status/{SID}
 * Either deviceId or crid must be non-empty. filterValue is optional.
 */
export const getLitControlStatus = ({ accountId, deviceId, crid, filterValue }) => {
  const params = new URLSearchParams();
  if (deviceId) params.set("deviceId", deviceId);
  if (crid) params.set("crid", crid);
  if (filterValue) params.set("filterValue", filterValue);

  if (!accountId || (!deviceId && !crid)) {
    return Promise.reject(new Error("Subscriber ID and either Device ID or Control Request ID are required"));
  }

  return callAccedoAPI(
    `${accedoBase()}/lit/control/status/${encodeURIComponent(accountId)}?${params.toString()}`,
    "GET"
  ).then((response) => {
    if (!Array.isArray(response)) {
      throw new Error("Invalid device control status response");
    }
    return response;
  });
};

const recordControl = (controlRequestID, action, accountId, deviceId, comment) => {
  const body = {
    controlRequestID,
    action,
    sid: accountId,
    cdsn: deviceId || accountId,
    project: "LIT",
  };
  if (comment) body.comment = comment;
  return callAPI("/control", "POST", body);
};

const fetchControlRecord = (controlRequestID) =>
  callAPI(
    `/control?controlRequestID=${encodeURIComponent(controlRequestID)}`,
    "GET"
  ).then((response) => {
    const data = response?.data ?? response;
    return Array.isArray(data) ? data[0] : data;
  });

const fetchMSTBControlComments = ({ accountId, deviceId }) => {
  const params = new URLSearchParams({
    sid: accountId,
    cdsn: deviceId,
  });
  return callAPI(`/lit/control?${params.toString()}`, "GET")
    .then((res) => {
      const rows = Array.isArray(res) ? res : res?.data ?? [];
      const map = new Map();
      rows.forEach((row) => {
        if (row.controlRequestID) map.set(row.controlRequestID, row);
      });
      return map;
    })
    .catch(() => new Map());
};

/**
 * PUT /control — update comment and/or resolved/unresolved state.
 * Only sends fields that are explicitly provided (not undefined).
 */
export const putControlComment = ({ controlRequestID, comment, resolved, unresolved }) => {
  const body = { controlRequestID };
  if (comment !== undefined) body.comment = comment;
  if (resolved !== undefined) body.resolved = resolved;
  if (unresolved !== undefined) body.unresolved = unresolved;
  return callAPI("/control", "PUT", body);
};

const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

const responseMessage = (response) =>
  response?.message || response?.data?.message || "";

const dispatchControlStatus = (dispatch, action, record) => {
  if (action === "logout") {
    dispatch({ type: LIT_APP_LOGOUT_STATUS, payload: record });
  } else if (action === "clearCache") {
    dispatch({ type: LIT_CLEAR_CACHE_STATUS, payload: record });
  }
};

const fetchSubmittedControlStatus = ({ dispatch, accountId, controlRequestID, action }) =>
  Promise.all([
    getLitControlStatus({ accountId, crid: controlRequestID }),
    fetchControlRecord(controlRequestID).catch(() => null),
  ])
    .then(([records, controlRecord]) => {
      if (records[0]) {
        dispatchControlStatus(dispatch, action, {
          ...records[0],
          ...(controlRecord || {}),
        });
      }
      return records;
    })
    .catch((err) => {
      dispatch({
        type: action === "logout" ? LIT_APP_LOGOUT_STATUS_FAILURE : LIT_CLEAR_CACHE_STATUS_FAILURE,
        payload: errorMessage(err, "Failed to load device control status"),
      });
      return [];
  });

export const triggerLitAppLogout = ({ accountId, deviceId, ticketId, comment }) => (dispatch) => {
  dispatch({ type: LIT_APP_LOGOUT_REQUEST });
  return sendLitControl({ accountId, deviceId, ticketId, action: "logout" })
    .then((res) => {
      const controlRequestID = res?.body?.controlRequestID;
      const recordPromise = recordControl(
        controlRequestID,
        "logout",
        accountId,
        deviceId,
        comment
      ).catch(() => undefined);
      dispatch({ type: LIT_APP_LOGOUT_SUCCESS, message: responseMessage(res) });
      return recordPromise.then(() =>
        fetchSubmittedControlStatus({ dispatch, accountId, controlRequestID, action: "logout" })
      );
    })
    .catch((err) => {
      console.log(err?.response?.data?.message);
      dispatch({
        type: LIT_APP_LOGOUT_FAILURE,
        payload: new Date().getTime(),
      });
    });
};

export const triggerLitClearCache = ({ accountId, deviceId, ticketId, comment }) => (dispatch) => {
  dispatch({ type: LIT_CLEAR_CACHE_REQUEST });
  return sendLitControl({ accountId, deviceId, ticketId, action: "clearCache" })
    .then((res) => {
      const controlRequestID = res?.body?.controlRequestID;
      const recordPromise = recordControl(
        controlRequestID,
        "clearCache",
        accountId,
        deviceId,
        comment
      ).catch(() => undefined);
      dispatch({ type: LIT_CLEAR_CACHE_SUCCESS, message: responseMessage(res) });
      return recordPromise.then(() =>
        fetchSubmittedControlStatus({ dispatch, accountId, controlRequestID, action: "clearCache" })
      );
    })
    .catch((err) => {
      console.log(err?.response?.data?.message);
      dispatch({
        type: LIT_CLEAR_CACHE_FAILURE,
        payload: new Date().getTime(),
      });
    });
};

export const fetchLitControlHistory = ({ accountId, deviceId, filterValue }) => (dispatch) => {
  dispatch({ type: LIT_CONTROL_HISTORY_REQUEST });

  if (!accountId || !deviceId) {
    dispatch({
      type: LIT_CONTROL_HISTORY_FAILURE,
      payload: "Subscriber ID and Device ID are required",
    });
    return Promise.resolve();
  }

  return getLitControlStatus({ accountId, deviceId, filterValue })
    .then((rows) => {
      let controlEventsList = [];
      try {
        controlEventsList =
          JSON.parse(localStorage.getItem("AppConfigControlResponseLIT"))?.body?.ConfigData?.data || [];
      } catch (_) {}

      return Promise.all([
        callAccedoAPI(`${accedoBase()}/globalConfig/receptionTimeout`, "GET").catch(() => null),
        callAccedoAPI(`${accedoBase()}/healthCheck`, "GET").catch(() => null),
        fetchMSTBControlComments({ accountId, deviceId }),
      ]).then(([receptionTimeout, serverTime, commentMap]) => {
        const serverMs = serverTime ? Date.parse(serverTime.time) : null;

        const classified = rows.map((row) => {
          // Merge MSTB comment fields onto the ACEEDO row
          const mstbRecord = commentMap.get(row.controlRequestID);
          const withComment = mstbRecord
            ? {
                ...row,
                comment: mstbRecord.comment ?? null,
                resolved: mstbRecord.resolved ?? false,
                unresolved: mstbRecord.unresolved ?? false,
              }
            : { ...row, comment: row.comment ?? null, resolved: row.resolved ?? false, unresolved: row.unresolved ?? false };

          if (serverMs === null) return withComment;

          const elapsed = serverMs - withComment.timestamp;

          if (withComment.status === "sent" && receptionTimeout) {
            if (elapsed > (receptionTimeout.control + 1) * 60000) {
              return { ...withComment, status: "failed" };
            }
          } else if (withComment.status === "received") {
            const failsAfter = controlEventsList.find((e) => e.event === withComment.action)
              ?.controlEventRequestFailsAfter;
            if (failsAfter != null && elapsed > failsAfter * 60000) {
              return { ...withComment, status: "failed" };
            }
          }

          return withComment;
        });

        dispatch({ type: LIT_CONTROL_HISTORY_SUCCESS, payload: classified });
      });
    })
    .catch((err) =>
      dispatch({
        type: LIT_CONTROL_HISTORY_FAILURE,
        payload: errorMessage(err, "Failed to load device control status"),
      })
    );
};

export const triggerLitAccountRefresh = ({ accountId, ticketId }) => (dispatch) => {
  dispatch({ type: LIT_ACCOUNT_REFRESH_REQUEST });
  const body = {};
  if (ticketId) body.ticketId = ticketId;
  return callAPI(
    `/lit/account-refresh/${encodeURIComponent(accountId)}`,
    "POST",
    body
  )
    .then((res) => {
      dispatch({
        type: LIT_ACCOUNT_REFRESH_SUCCESS,
        payload: res?.data ?? res,
        message: responseMessage(res),
      });
      return res;
    })
    .catch((err) =>
      dispatch({
        type: LIT_ACCOUNT_REFRESH_FAILURE,
        payload: errorMessage(err, "Failed to submit account refresh"),
      })
    );
};

export const litAppLogoutControlStatus = (data) => ({ type: LIT_APP_LOGOUT_STATUS, payload: data });
export const litClearCacheControlStatus = (data) => ({ type: LIT_CLEAR_CACHE_STATUS, payload: data });

export const litAccountRefreshControlStatus = (data) => ({ type: LIT_ACCOUNT_REFRESH_STATUS, payload: data });
